import _ from "lodash";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import classes from "./ParentProgressReport.scss";
import { goToRelativeRoute } from "modules/Services";
import { getIsStudentReflectionEnabledInReports } from "ProgressReportCommon/utils";
import TopHeader from "./TopHeader";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import ReportItem from "./ReportItem";
import ParentProgressReportV2 from "./ParentProgressReportV2";
import {
  getStudentProgressReportFeedQuery,
  getStudentProgressReportReflectionsQuery,
} from "../modules/ParentProgressReportQueries";
import { getStudentProgressReportFeedFromCache } from "../modules/ParentProgressReportGraphqlHelpers";
import { withCombinedAccessBlocker } from "lib/PermissionAware";
import NoDataComponent from "UIComponents/NoDataComponent";
import { CURRICULUM_TYPE_PYP } from "Constants/stringConstants";
import { setActiveHomepageCard } from "Teacher/modules/TeacherModule";
import { PROGRESS_REPORTS_PROGRAMME_LEVEL_VIEW_PERMISSION } from "Constants/permissionConstants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

const ProgressReport = props => {
  const {
    t,
    goToRelativeRoute,
    publishedProgressReports,
    setActiveHomepageCard,
    isStudentReflectionEnabled,
    studentReflections,
    userType,
  } = props;
  const progressReportsCount = _.size(publishedProgressReports);
  const isStudentWithReflections =
    isStudentReflectionEnabled && userType === "student";

  const renderReports = () => {
    return (
      <div className={classes.reportContainer}>
        {progressReportsCount && (
          <div className={classes.count}>
            {t("progressReport:progress_report_count", {
              count: progressReportsCount,
            })}
          </div>
        )}
        {_.map(publishedProgressReports, (report, index) => {
          const studentProgressReportId = _.get(
            report,
            "studentProgressReport.id",
            ""
          );
          const publishedProgressReportId = _.get(report, "id", "");
          const publishedAt = _.get(report, "publishedAt", "");

          const curriculumProgramType = _.get(
            report,
            "curriculumProgram.type",
            CURRICULUM_TYPE_PYP
          );

          const reportTitle = _.get(
            report,
            "studentProgressReport.courseProgressReportTask.title"
          );

          return (
            <ReportItem
              key={publishedProgressReportId}
              studentProgressReportId={studentProgressReportId}
              publishedProgressReportId={publishedProgressReportId}
              filteredNodes={report.filteredNodes}
              publishedAt={publishedAt}
              index={index}
              curriculumProgramType={curriculumProgramType}
              reportTitle={reportTitle}
            />
          );
        })}
      </div>
    );
  };

  const renderNoDataComp = () => {
    return (
      <div className={classes.noDataContainer}>
        <NoDataComponent
          emptyText={""}
          emptyHeaderText={t("progressReport:no_progress_reports")}
          isSearch={false}
        />
      </div>
    );
  };

  useEffect(() => {
    setActiveHomepageCard({
      from: "home-page",
      label: t("teacherHomePage:progress_report"),
    });
  }, []);

  if (props.children) {
    return props.children;
  }

  if (isStudentWithReflections) {
    return (
      <div className={classes.topContainer}>
        <ParentProgressReportV2
          publishedProgressReports={publishedProgressReports}
          studentReflections={studentReflections}
        />
      </div>
    );
  }

  return (
    <div className={classes.topContainer}>
      <TopHeader
        title={t("progressReport:progress_report_plural")}
        onGoBackClick={() => goToRelativeRoute({ route: "../" })}
        showToddleIcon={true}
      />
      {!progressReportsCount ? renderNoDataComp() : renderReports()}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const userType = _.get(userInfo, "user_type");
  const selectedAcademicYear = _.get(
    state,
    "platform.academicYearSelected",
    {}
  );
  const { id: selectedAcademicYearId } = selectedAcademicYear;
  let id;
  switch (userType) {
    case "student": {
      id = userInfo.id;
      break;
    }
    case "parent": {
      id = userInfo.childID;
      break;
    }
  }

  const curriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    null
  );
  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    null
  );
  const curriculumProgramIds = curriculumProgramId
    ? [curriculumProgramId]
    : null;

  const isStudentReflectionEnabled = getIsStudentReflectionEnabledInReports();

  return {
    userId: id,
    isAccessBlocked: userType == "staff",
    controlParameterObj: {
      type: "MODULE_SETTINGS",
      controlParameter: PROGRESS_REPORTS_PROGRAMME_LEVEL_VIEW_PERMISSION,
    },
    selectedAcademicYearId,
    curriculumProgramId,
    curriculumProgramIds,
    userType,
    curriculumProgramType,
    isStudentReflectionEnabled,
  };
};

const mapActionCreators = {
  goToRelativeRoute,
  setActiveHomepageCard,
};

export default compose(
  I18nHOC({
    resource: ["progressReport"],
  }),
  connect(mapStateToProps, mapActionCreators),
  withCombinedAccessBlocker,
  graphql(getStudentProgressReportFeedQuery, {
    name: "getStudentProgressReportFeed",
    options: ({ userId, selectedAcademicYearId, curriculumProgramIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        filters: {
          academicYear: selectedAcademicYearId,
          curriculumProgramIds,
        },
      },
    }),
    props: ({
      getStudentProgressReportFeed,
      ownProps: { userId, selectedAcademicYearId, curriculumProgramIds },
    }) => {
      const progressReportFeedFromCache = getStudentProgressReportFeedFromCache(
        {
          id: userId,
          filters: {
            academicYear: selectedAcademicYearId,
            curriculumProgramIds,
          },
        }
      );

      const publishedProgressReports = _.get(
        progressReportFeedFromCache,
        "publishedProgressReports",
        []
      );

      return {
        isLoading:
          getStudentProgressReportFeed["networkStatus"] == 1 ||
          getStudentProgressReportFeed["networkStatus"] == 2,
        isData: !_.isEmpty(progressReportFeedFromCache),
        publishedProgressReports,
      };
    },
  }),
  graphql(getStudentProgressReportReflectionsQuery, {
    name: "getStudentReflections",
    skip: ({ isStudentReflectionEnabled, userType }) =>
      !isStudentReflectionEnabled || userType !== "student",
    options: ({ userId, selectedAcademicYearId, curriculumProgramIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        filters: {
          academicYearId: selectedAcademicYearId,
          curriculumProgramIds: curriculumProgramIds || [],
        },
      },
      context: { useHttpLink: true },
    }),
    props: ({ getStudentReflections, ownProps: { isData, isLoading } }) => {
      const studentReflections = _.get(
        getStudentReflections,
        "node.studentProgressReportReflections",
        []
      );
      return {
        studentReflections,
        isData: !_.isEmpty(_.get(getStudentReflections, "node")) && isData,
        isLoading:
          _.includes([1, 2], _.get(getStudentReflections, "networkStatus")) ||
          isLoading,
      };
    },
  }),
  withLoader
)(ProgressReport);
